import { useEffect, useRef } from 'react'
import MonacoEditor, { OnMount } from '@monaco-editor/react'

const FIELD_NAMES = [
    'firstName',
    'lastName',
    'fullName',
    'linkedInUrl',
    'headline',
    'summary',
    'skills',
    'location',
    'industry',
    'jobDescription',
    'jobTitle',
    'company',
    'currentJobTitle',
    'currentCompany',
    'pastJobTitle',
    'pastCompany',
]

type FieldSearchEditorProps = {
    value: string
    onChange: (newValue: string) => void
    onSearch: (query: string) => void
}

export default function FieldSearchEditor({
    value,
    onChange,
    onSearch,
}: FieldSearchEditorProps) {
    const completionProviderDisposableRef = useRef<any>(null)

    const handleEditorMount: OnMount = (editor, monacoInstance) => {
        // Clean up any existing provider to avoid duplicates
        if (completionProviderDisposableRef.current) {
            completionProviderDisposableRef.current.dispose()
        }

        completionProviderDisposableRef.current =
            monacoInstance.languages.registerCompletionItemProvider(
                'plaintext',
                {
                    triggerCharacters: [...'abcdefghijklmnopqrstuvwxyz'],
                    provideCompletionItems: (model, position) => {
                        const lineContent = model.getLineContent(
                            position.lineNumber,
                        )
                        const cursorPos = position.column - 1
                        const textBeforeCursor = lineContent.substring(
                            0,
                            cursorPos,
                        )
                        const match = textBeforeCursor.match(/(\w+)$/)
                        const prefix = match?.[1] ?? ''

                        if (!prefix)
                            return { suggestions: [], isIncomplete: false }

                        const lowerPrefix = prefix.toLowerCase()

                        const matchedFields = FIELD_NAMES.filter((field) =>
                            field.toLowerCase().includes(lowerPrefix),
                        ).sort((a, b) => {
                            const aIndex = a.toLowerCase().indexOf(lowerPrefix)
                            const bIndex = b.toLowerCase().indexOf(lowerPrefix)
                            return aIndex - bIndex || a.localeCompare(b)
                        })

                        const range = {
                            startLineNumber: position.lineNumber,
                            endLineNumber: position.lineNumber,
                            startColumn: cursorPos - prefix.length + 1,
                            endColumn: position.column,
                        }

                        const suggestions = matchedFields.map((field) => ({
                            label: field,
                            kind: monacoInstance.languages.CompletionItemKind
                                .Property,
                            insertText: `${field}:`,
                            insertTextRules:
                                monacoInstance.languages
                                    .CompletionItemInsertTextRule
                                    .InsertAsSnippet,
                            range,
                            filterText: prefix,
                        }))

                        return { suggestions, isIncomplete: false }
                    },
                },
            )

        editor.addAction({
            id: 'trigger-search',
            label: 'Trigger Search',
            keybindings: [
                monacoInstance.KeyMod.CtrlCmd | monacoInstance.KeyCode.Enter,
            ],
            run: (editor) => {
                const currentValue = editor.getValue()
                onSearch(currentValue)
            },
        })
    }

    // Clean up on unmount
    useEffect(() => {
        return () => {
            if (completionProviderDisposableRef.current) {
                completionProviderDisposableRef.current.dispose()
            }
        }
    }, [])

    return (
        <div
            className="border rounded-md overflow-hidden py-2"
            style={{ height: '200px' }}
        >
            <MonacoEditor
                height="100%"
                defaultLanguage="plaintext"
                value={value}
                onChange={(val) => onChange(val ?? '')}
                onMount={handleEditorMount}
                options={{
                    fontSize: 14,
                    minimap: { enabled: false },
                    wordBasedSuggestions: 'off',
                    suggestOnTriggerCharacters: true,
                    lineNumbers: 'off',
                    quickSuggestions: {
                        other: true,
                        comments: false,
                        strings: true,
                    },
                    quickSuggestionsDelay: 100,
                    suggest: {
                        filterGraceful: false,
                        matchOnWordStartOnly: true,
                    },
                    renderLineHighlight: 'none',
                    scrollBeyondLastLine: false,
                    autoClosingBrackets: 'never',
                    scrollbar: {
                        vertical: 'auto',
                        horizontal: 'auto',
                        verticalScrollbarSize: 8,
                        verticalSliderSize: 8,
                        horizontalScrollbarSize: 8,
                        horizontalSliderSize: 8,
                        handleMouseWheel: true,
                        useShadows: false,
                    },
                }}
            />
        </div>
    )
}
